import * as Sentry from '@sentry/react';
import { forwardRef, type MouseEvent } from 'react';
import {
  Area,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  XAxis,
  YAxis,
} from 'recharts';
import { TargetComparison } from '../../../../api/targets-client/targets-client.type';
import { skyLight } from '../../../../styles/design-tokens';
import { LineChartDot } from './line-chart-dot';
import { LineChartLegend } from './line-chart-legend';
import { LineChartTooltip } from './line-chart-tooltip';
import { useItemColors } from './line-chart.hooks';
import type { ChartDataItem, LineChartProps } from './line-chart.type';

const TARGET_FILL_OPACITY = 0.6;
const TARGET_STROKE_OPACITY = 1;

export const LineChart = forwardRef<HTMLDivElement, LineChartProps>(
  (
    {
      chartData,
      selectedItems,
      selectedTrends = [],
      selectedTargets = [],
      hoveredItem,
      setHoveredItem,
      onPointClick,
      yAxisConfig = {},
      formatItemName = (name) => name,
      formatValue,
      labelMaxLength,
    },
    ref,
  ) => {
    const itemColors = useItemColors(selectedItems);

    const handleChartPointClick = (itemId: string, event: MouseEvent<SVGCircleElement>) => {
      if (!onPointClick) return;

      try {
        const { payload } = event as unknown as { payload: ChartDataItem };

        if (!payload || !payload.rawDate) {
          console.error('Invalid payload structure in chart click event');
          return;
        }

        onPointClick(itemId, payload);
      } catch (error) {
        Sentry.captureException(error);
      }
    };

    const isHighlighted = (itemId: string) => {
      return hoveredItem === null || hoveredItem === itemId;
    };

    return (
      <div ref={ref} style={{ width: '100%', height: '100%', minHeight: 160 }}>
        <ResponsiveContainer width="100%" height="100%" minHeight={160}>
          <ComposedChart data={chartData} margin={{ top: 20, right: 10 }}>
            <defs>
              {selectedTargets.map((target) => (
                <linearGradient key={`gradient-${target.id}`} id={`color-${target.id}`} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="20%" stopColor={target.color} stopOpacity={TARGET_FILL_OPACITY} />
                  <stop offset="80%" stopColor={target.color} stopOpacity={TARGET_FILL_OPACITY * 0.5} />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke={skyLight} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
            />
            <YAxis
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              tick={{ fontSize: 12 }}
              axisLine={{ stroke: skyLight }}
              tickLine={{ stroke: skyLight }}
              label={
                yAxisConfig.label
                  ? {
                      value: yAxisConfig.label,
                      angle: -90,
                      position: 'insideLeft',
                      style: { textAnchor: 'middle' },
                      offset: 10,
                    }
                  : undefined
              }
            />
            <RechartsTooltip
              content={
                <LineChartTooltip
                  formatItemName={formatItemName}
                  formatValue={formatValue}
                  targetIds={selectedTargets.flatMap((target) => {
                    const baseId = target.id;
                    if (target.isRange) {
                      return [baseId, `${baseId}_lower`, `${baseId}_upper`];
                    }
                    return [baseId];
                  })}
                />
              }
            />

            {/* Target areas */}
            {selectedTargets.map((target) => {
              if (target.isRange) {
                return (
                  <Area
                    key={target.id}
                    dataKey={target.id}
                    stroke={target.color}
                    fill={`url(#color-${target.id})`}
                    fillOpacity={TARGET_FILL_OPACITY}
                    strokeOpacity={TARGET_STROKE_OPACITY}
                    strokeWidth={1}
                    strokeDasharray={target.strokeDasharray}
                    connectNulls={false}
                    isAnimationActive={false}
                    dot={false}
                    activeDot={false}
                  />
                );
              } else {
                return (
                  <Area
                    key={target.id}
                    dataKey={target.id}
                    stroke={target.color}
                    fill={target.target_comparison === TargetComparison.EQ ? 'transparent' : `url(#color-${target.id})`}
                    fillOpacity={TARGET_FILL_OPACITY}
                    strokeOpacity={TARGET_STROKE_OPACITY}
                    strokeWidth={1}
                    strokeDasharray={target.strokeDasharray}
                    connectNulls={false}
                    isAnimationActive={false}
                    dot={false}
                    activeDot={false}
                    baseValue={target.baseValue}
                  />
                );
              }
            })}

            {/* Regular lines */}
            {selectedItems.map((selectedItem) => {
              return (
                <Line
                  key={selectedItem.id}
                  type="monotone"
                  dataKey={selectedItem.id}
                  name={selectedItem.name}
                  stroke={itemColors[selectedItem.id]}
                  strokeWidth={2}
                  strokeOpacity={isHighlighted(selectedItem.id) ? 1 : 0.3}
                  dot={
                    <LineChartDot
                      color={itemColors[selectedItem.id]}
                      opacity={isHighlighted(selectedItem.id) ? 1 : 0.3}
                    />
                  }
                  activeDot={
                    onPointClick ? (
                      <LineChartDot
                        color={itemColors[selectedItem.id]}
                        opacity={isHighlighted(selectedItem.id) ? 1 : 0.3}
                        onClick={(_, event: any) => handleChartPointClick(selectedItem.id, event)}
                      />
                    ) : (
                      <LineChartDot
                        color={itemColors[selectedItem.id]}
                        opacity={isHighlighted(selectedItem.id) ? 1 : 0.3}
                      />
                    )
                  }
                  isAnimationActive={false}
                  connectNulls
                />
              );
            })}

            {/* Trend lines */}
            {selectedTrends.map((selectedItem) => {
              return (
                <Line
                  key={`${selectedItem.id}_trend`}
                  type="monotone"
                  dataKey={`${selectedItem.id}_trend`}
                  name={`${selectedItem.name} Trend`}
                  stroke={itemColors[selectedItem.id]}
                  strokeWidth={2}
                  strokeOpacity={isHighlighted(selectedItem.id) ? 1 : 0.3}
                  strokeDasharray="5 5"
                  dot={false}
                  activeDot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              );
            })}

            <Legend
              content={
                <LineChartLegend
                  selectedItems={selectedItems}
                  hoveredItem={hoveredItem}
                  setHoveredItem={setHoveredItem}
                  itemColors={itemColors}
                  formatItemName={formatItemName}
                  chartData={chartData}
                  labelMaxLength={labelMaxLength}
                />
              }
              verticalAlign="bottom"
              height={36}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    );
  },
);
